import { computed, set, setProperties } from '@ember/object';
import { empty, equal, or, reads } from '@ember/object/computed';
import { isEmberTesting } from 'ember-simplepractice/utils/is-testing';
import { relationshipName } from 'client-portal/utils/relationship-types';
import { service } from '@ember/service';
import { session } from 'client-portal/utils/persistent-hash-table';
import { sessionTimeoutMinutes } from 'client-portal/services/keepalive';
import { tracked } from '@glimmer/tracking';
import { warning } from 'client-portal/utils/banners';
import SimpleAuthSessionService from 'ember-simple-auth/services/session';
import classic from 'ember-classic-decorator';

export const MONARCH_CHANNEL = 'monarch';
export const UPRISE_CHANNEL = 'uprise';
export const CLIENT_PORTAL = 'client_portal';
export const PROFESSIONAL_WEBSITE = 'professional_website';
export const WEBSITE_WIDGET_CHANNEL = 'sp_website';

const THERAPYFINDER_EMBED_UI_CHANNELS = [MONARCH_CHANNEL, UPRISE_CHANNEL];
const TRACKING_SOURCE_VALUES = ['listing_page', 'clinician_profile_page'];
const FEATURE_TEST = 'test';
const FEATURE_NOEXP = 'noexp';
const UNDEFINED_VALUE = 'undefined';

@classic
export default class Session extends SimpleAuthSessionService {
  @service analytics;
  @service router;
  @service currentPractice;
  @service clientAppBanner;
  @service fastboot;
  @service clientTimezone;
  @service intl;
  @service bookingResume;

  @tracked channel;

  environment = null;

  #isHeaderFooterHidden = false;
  #trackingSource = null;
  #linkSource = null;
  #addMessage = false;
  #addReasonsForVisit = false;
  #newProspectiveClient = null;
  #clinicianReferral = null;

  #reorderedNav = FEATURE_NOEXP;
  #addReasonsForVisitFlag = FEATURE_NOEXP;
  #anonymousId = null;
  #referralChannel = null;
  _noInvalidationBanner = false;

  #sourceNames = {
    'client_portal': 'Client Portal',
    'embedded_widget': 'Embedded',
    'preview_widget': 'Preview',
    'monarch': 'Therapy Finder',
    'professional_website': 'Professional Website',
  };

  set anonymousId(value) {
    this.#anonymousId = this.isMonarchEmbedded && value;
  }

  get anonymousId() {
    return this.#anonymousId;
  }

  set addMessage(value) {
    this.#addMessage = this.isMonarchEmbedded && value;
  }

  get addMessage() {
    return this.#addMessage;
  }

  set addReasonsForVisit(value) {
    this.#addReasonsForVisit = this.isMonarchEmbedded && value;
  }

  get addReasonsForVisit() {
    return this.#addReasonsForVisit;
  }

  set addReasonsForVisitFlag(value) {
    this.#addReasonsForVisitFlag = this.isMonarchEmbedded && value;
  }

  get addReasonsForVisitFlag() {
    return this.#addReasonsForVisitFlag;
  }

  set clinicianReferral(value) {
    this.#clinicianReferral = this.isMonarchEmbedded && value;
  }

  get clinicianReferral() {
    return this.#clinicianReferral;
  }

  set trackingSource(value) {
    if (this.isMonarchEmbedded && TRACKING_SOURCE_VALUES.includes(value)) {
      this.#trackingSource = value;
    }
  }

  set linkSource(value) {
    if (!value) return;
    this.#linkSource = value;
  }

  get trackingSource() {
    return this.#trackingSource;
  }

  get linkSource() {
    return this.#linkSource;
  }

  set reorderedNav(value) {
    if (typeof value !== UNDEFINED_VALUE) {
      this.#reorderedNav = this.isMonarchEmbedded && value;
    }
  }

  get reorderedNav() {
    return this.#reorderedNav;
  }

  get reorderedNavEnabled() {
    return this.#reorderedNav === FEATURE_TEST;
  }

  set isHeaderFooterHidden(value) {
    this.#isHeaderFooterHidden = value;
    if (!this.fastboot.isFastBoot) {
      session.set('isHeaderFooterHidden', !!value || '');
    }
  }

  set newProspectiveClient(client) {
    this.#newProspectiveClient = client;
  }

  get newProspectiveClient() {
    return this.#newProspectiveClient;
  }

  get siteName() {
    return this.channel ?? CLIENT_PORTAL;
  }

  set referralChannel(value) {
    if (!value) return;
    this.#referralChannel = value;
  }

  get referralChannel() {
    return this.#referralChannel;
  }

  @computed('currentClient', 'newProspectiveClient')
  get isExistingClient() {
    return this.currentClient && this.newProspectiveClient?.id !== this.currentClient.id;
  }

  get isNonActiveClientSignedIn() {
    let { currentClient, currentClientAccess } = this;
    let isFullySignedIn = currentClientAccess && !currentClientAccess.isOtpPending;

    return Boolean(currentClient?.isNonActiveStatus && isFullySignedIn);
  }
  get isNewClientBookingFlow() {
    return this.isNonActiveClientSignedIn || !this.isExistingClient;
  }

  @computed('fastboot.isFastBoot')
  get isHeaderFooterHidden() {
    if (!this.fastboot.isFastBoot && !this.#isHeaderFooterHidden) {
      return Boolean(session.get('isHeaderFooterHidden'));
    }
    return this.#isHeaderFooterHidden;
  }

  @computed('isProfessionalWebsite', 'isMonarchEmbedded', 'isClientPortal', 'isWebsiteWidget')
  get isClientPortalMonarchOrSpWebsite() {
    let { isProfessionalWebsite, isMonarchEmbedded, isClientPortal, isWebsiteWidget } = this;
    return isMonarchEmbedded || isProfessionalWebsite || isClientPortal || isWebsiteWidget;
  }

  @reads('currentPractice.practiceWebsite') isPracticeWebsite;

  @reads('environment.currentClientAccess') currentClientAccess;

  @reads('environment.currentClient') currentClient;

  @reads('environment.currentClientOptions') currentClientOptions;

  @reads('currentClientAccess.client.signatoryName') signatoryName;

  @reads('currentClient.relationshipToCurrentClientAccess') clientRelationshipType;

  @equal('channel', WEBSITE_WIDGET_CHANNEL) isWebsiteWidget;

  @equal('channel', PROFESSIONAL_WEBSITE) isProfessionalWebsite;

  @equal('channel', MONARCH_CHANNEL) isMonarchEmbedded;

  @computed('channel')
  get isTherapyfinderEmbedUi() {
    return THERAPYFINDER_EMBED_UI_CHANNELS.includes(this.channel);
  }

  @equal('channel', CLIENT_PORTAL) isChannelClientPortal;

  @empty('channel') isChannelEmpty;

  @or('isChannelClientPortal', 'isChannelEmpty') isClientPortal;

  @reads('router.currentRouteName') currentRouteName;

  @reads('currentPractice.canUseNativeDocRendering') canUseNativeDocRendering;

  @reads('currentPractice.hasClientappDocumentAccess') hasClientappDocumentAccess;

  @reads('currentPractice.showPaymentSelection') showPaymentSelection;
  @reads('currentPractice.showPrescreenerSection') showPrescreenerSection;

  @computed('channel')
  get isWidgetChannel() {
    return this.channel?.includes('widget');
  }

  @computed('clientRelationshipType')
  get clientRelationshipName() {
    return relationshipName(this.clientRelationshipType);
  }

  @computed('currentRouteName', 'isHeaderFooterHidden', 'hasClientappDocumentAccess')
  get isDocumentsRouteRedirect() {
    let { currentRouteName, isHeaderFooterHidden, hasClientappDocumentAccess } = this;

    return (
      currentRouteName.includes('documents') && hasClientappDocumentAccess && isHeaderFooterHidden
    );
  }

  @computed('currentRouteName')
  get isWidget() {
    return this.currentRouteName.includes('widget');
  }

  @computed('isWidget')
  get canUsePaymentMethods() {
    return !this.isWidget;
  }

  @computed('currentRouteName')
  get isConfirmingAppointment() {
    return /(request|widget)\.confirmation/i.test(this.currentRouteName ?? '');
  }

  @computed('isChannelEmpty', 'currentRouteName', 'isWidget', 'channel')
  get sourceName() {
    if (this.isChannelEmpty) {
      return this.currentRouteName && this.isWidget ? 'Embedded' : 'Client Portal';
    } else {
      return this.#sourceNames[this.channel] || `Partner Channel: ${this.channel}`;
    }
  }

  signedOutBanner() {
    warning({
      title: this.intl.t('session.signed_out_banner_title', { minutes: sessionTimeoutMinutes }),
      isTransient: false,
    });
  }

  clearProspectiveClient() {
    set(this, 'newProspectiveClient', null);
  }

  async signOut() {
    setProperties(this.data, {
      newProspectiveClient: null,
      skipLicenseAgreement: false,
      skipLegalAgreements: false,
      redirect: undefined,
      agreementResumeRedirect: undefined,
      recentlyBookedSlot: null,
    });
    this.bookingResume.reset();

    this.clientAppBanner.resetDismiss();
    this.clientTimezone.reset();

    this.analytics.clearAttributes();

    let restartAppNeeded =
      this.currentPractice.get('hasClientappDocumentAccess') ||
      this.currentPractice.get('canUseNativeDocRendering');

    let redirectPath;
    if (restartAppNeeded) {
      redirectPath = '/session-timeout';
    } else if (this._noInvalidationBanner) {
      redirectPath = '/';
    } else {
      redirectPath = '/sign-in?signedOut';
    }

    if (this.fastboot.isFastBoot) {
      this.fastboot.response.statusCode = 302;
      this.fastboot.response.headers.set('location', redirectPath);
    } else {
      session.remove('isHeaderFooterHidden');

      let newLocation = this._isSignInPath ? location.href : redirectPath;
      if (isEmberTesting()) {
        await this.router.transitionTo(newLocation);
      } else {
        if (location.href === newLocation) {
          location.reload();
        } else {
          location.href = newLocation;
        }
      }
    }
  }

  handleAuthentication() {
    // noop
  }

  handleInvalidation() {
    super.handleInvalidation(...arguments);
    return this.signOut();
  }

  get _isSignInPath() {
    let currentPath = this.fastboot.isFastBoot ? this.fastboot.request.path : location.pathname;
    return currentPath && currentPath.indexOf('/sign-in') === 0;
  }
}
